import { useCallback, useEffect, useRef, useState } from 'react';

import {
  MAX_VOICE_NOTE_MS,
  micBlockedReason,
  MIN_VOICE_NOTE_MS,
  RECORD_FORMATS,
  STORED_BARS,
  toWaveform,
  type MicProblem,
  type VoiceClip,
} from './voiceNote.js';

export type StopReason = 'manual' | 'max_length' | 'interrupted';

export interface RecorderEvents {
  onClip: (clip: VoiceClip, reason: StopReason) => void;
  /** Recording could not start; `errorName` is the raw DOMException name. */
  onBlocked: (errorName: string) => void;
  onTooShort: (durationMs: number) => void;
}

export type MicPermission = PermissionState | 'unknown';

interface FramePolicy {
  allowsFeature(feature: string): boolean;
}

const CLOCK_TICK_MS = 250;
const DECODE_RATE = 16_000;

const SUPPORTED =
  typeof window !== 'undefined' &&
  window.isSecureContext &&
  typeof MediaRecorder !== 'undefined' &&
  Boolean(navigator.mediaDevices?.getUserMedia);

/** A host iframe without `allow="microphone"` blocks the mic before any site setting can. */
function frameAllowsMic(): boolean {
  const doc = document as { permissionsPolicy?: FramePolicy; featurePolicy?: FramePolicy };
  const policy = doc.permissionsPolicy ?? doc.featurePolicy;
  return policy ? policy.allowsFeature('microphone') : true;
}

type Phase = 'idle' | 'asking' | 'recording';

interface Session {
  recorder: MediaRecorder;
  clock: number;
  cap: number;
  startedAt: number;
  stoppedAt?: number;
  reason: StopReason;
  chunks: Blob[];
  keep: boolean;
}

async function waveformOf(blob: Blob): Promise<number[]> {
  const decoder = new OfflineAudioContext(1, 1, DECODE_RATE);
  const audio = await decoder.decodeAudioData(await blob.arrayBuffer());
  return toWaveform(audio.getChannelData(0), STORED_BARS);
}

function errorName(error: unknown): string {
  return error instanceof Error ? error.name : 'Error';
}

export function useVoiceRecorder(events: RecorderEvents) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [problem, setProblem] = useState<MicProblem | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const session = useRef<Session | null>(null);
  const permission = useRef<PermissionStatus | null>(null);
  const asking = useRef(false);
  const abandoned = useRef(false);
  const dismissedOnce = useRef(false);
  const mounted = useRef(true);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  useEffect(() => {
    if (!SUPPORTED || !navigator.permissions) return;
    let gone = false;
    const sync = () => {
      const denied = permission.current?.state === 'denied';
      setBlocked(denied);
      if (!denied) setProblem((current) => (current === 'blocked' ? null : current));
    };
    navigator.permissions.query({ name: 'microphone' as PermissionName }).then(
      (result) => {
        if (gone) return;
        permission.current = result;
        sync();
        result.addEventListener('change', sync);
      },
      () => undefined,
    );
    return () => {
      gone = true;
      permission.current?.removeEventListener('change', sync);
    };
  }, []);

  const stop = useCallback((reason: StopReason = 'manual') => {
    const current = session.current;
    if (!current || current.recorder.state === 'inactive') return;
    current.stoppedAt = Date.now();
    current.reason = reason;
    current.recorder.stop();
  }, []);

  /** Drops the recording and returns how long it ran. */
  const cancel = useCallback((): number => {
    const current = session.current;
    if (!current) {
      abandoned.current = asking.current;
      return 0;
    }
    current.keep = false;
    stop();
    return (current.stoppedAt ?? Date.now()) - current.startedAt;
  }, [stop]);

  /** A first closed prompt is "not now" and says nothing; Firefox reports a repeat as `prompt` too. */
  const diagnose = (error: unknown): MicProblem | null => {
    const reason = micBlockedReason(errorName(error));
    if (reason === 'no_device') return 'missing';
    if (reason === 'in_use') return 'busy';
    if (reason !== 'permission_denied') return 'failed';
    if (!frameAllowsMic()) return 'frame';
    if (/system/i.test((error as Error).message)) return 'system';
    const state = permission.current?.state;
    if (state === 'denied') return 'blocked';
    if (state === 'granted') return 'system';
    if (dismissedOnce.current) return 'blocked';
    dismissedOnce.current = true;
    return null;
  };

  const start = useCallback(async () => {
    if (!SUPPORTED || session.current || asking.current) return;
    asking.current = true;
    abandoned.current = false;
    setProblem(null);
    setPhase('asking');

    let stream: MediaStream;
    let recorder: MediaRecorder;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      asking.current = false;
      if (!mounted.current) return;
      setPhase('idle');
      if (abandoned.current) return;
      setProblem(diagnose(error));
      eventsRef.current.onBlocked(errorName(error));
      return;
    }
    asking.current = false;
    const release = () => stream.getTracks().forEach((track) => track.stop());
    if (!mounted.current) return release();
    if (abandoned.current) {
      setPhase('idle');
      return release();
    }
    try {
      const mimeType = RECORD_FORMATS.find((format) => MediaRecorder.isTypeSupported(format));
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (error) {
      release();
      setPhase('idle');
      setProblem('failed');
      eventsRef.current.onBlocked(errorName(error));
      return;
    }
    dismissedOnce.current = false;

    const startedAt = Date.now();
    const current: Session = {
      recorder,
      startedAt,
      reason: 'manual',
      chunks: [],
      keep: true,
      clock: window.setInterval(
        () => setElapsedMs(Math.floor((Date.now() - startedAt) / 1000) * 1000),
        CLOCK_TICK_MS,
      ),
      cap: window.setTimeout(() => stop('max_length'), MAX_VOICE_NOTE_MS),
    };

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) current.chunks.push(event.data);
    };
    recorder.onerror = () => stop('interrupted');
    recorder.onstop = async () => {
      window.clearInterval(current.clock);
      window.clearTimeout(current.cap);
      release();
      const durationMs = Math.min(
        (current.stoppedAt ?? Date.now()) - current.startedAt,
        MAX_VOICE_NOTE_MS,
      );
      const kept = current.keep && durationMs >= MIN_VOICE_NOTE_MS;
      const blob = new Blob(current.chunks, { type: recorder.mimeType });
      const waveform = kept ? await waveformOf(blob).catch(() => []) : [];
      // Held through the decode, so a Cancel pressed meanwhile still reaches `keep`.
      if (session.current === current) session.current = null;
      if (!mounted.current) return;
      setPhase('idle');
      setElapsedMs(0);
      setStream(null);
      if (current.keep && !kept) eventsRef.current.onTooShort(durationMs);
      if (kept && current.keep) {
        eventsRef.current.onClip(
          { blob, url: URL.createObjectURL(blob), durationMs, waveform },
          current.reason,
        );
      }
    };
    stream.getAudioTracks()[0]?.addEventListener('ended', () => {
      setProblem('lost');
      stop('interrupted');
    });

    session.current = current;
    recorder.start();
    setStream(stream);
    setPhase('recording');
  }, [stop]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (session.current) session.current.keep = false;
      stop();
    };
  }, [stop]);

  return {
    supported: SUPPORTED,
    phase,
    problem,
    blocked,
    elapsedMs,
    stream,
    start,
    stop,
    cancel,
    clearProblem: useCallback(() => setProblem(null), []),
    permission: (): MicPermission => permission.current?.state ?? 'unknown',
  };
}
